import React, { useLayoutEffect, useRef, useState } from "react";

interface ChartFrameProps {
  children: (size: { width: number; height: number }) => React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * ChartFrame: A fixed-height (256px) container that observes its own size and triggers a resize event for its children.
 * Debounces resize events by ~140ms. Use as a drop-in replacement for ResponsiveContainer.
 */
export const ChartFrame: React.FC<ChartFrameProps> = ({ children, className = "", style }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 256 });
  const timerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const raw = e.contentRect.width;
        const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1));
        const snapped = Math.floor(raw * dpr) / dpr;

        // Snap to an 8px grid to prevent tiny width oscillations causing axis recalculation
        const grid = 8;
        const gridSnapped = Math.max(0, Math.round(snapped / grid) * grid);

        const apply = () => {
          setSize((prev) => ({
            width: Math.abs(gridSnapped - prev.width) >= 6 ? gridSnapped : prev.width,
            height: 256,
          }));
          timerRef.current = null;
        };

        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(apply, 140);
      }
    });

    ro.observe(el);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`w-full min-w-0 h-[256px] overflow-hidden chart-stable ${className}`}
      style={{ height: "256px", minHeight: "256px", contain: "layout paint", ...style }}
    >
      {size.width > 0 && children(size)}
    </div>
  );
};
